/**
 * Plan Limits Configuration
 *
 * Defines the limits for each subscription plan and Stripe integration.
 *
 * BILLING MODEL:
 * - Payout billing uses Stripe metered pricing with SUM aggregation.
 * - Meter event value = payout amount in dollars (e.g., 5000 for a $5,000 payout).
 * - Stripe per-unit price ($0.035/dollar) implements an effective 3.5% fee.
 * - Included volume and overages are handled ENTIRELY in Stripe via tiered pricing.
 * - Application code does NOT enforce included volume (UI display only).
 */

/**
 * Default payout threshold in cents ($5).
 * Used when an app hasn't configured a custom minPayoutCents.
 */
export const DEFAULT_PAYOUT_THRESHOLD_CENTS = 500;

export const PLAN_LIMITS = {
	bootstrap: {
		apps: 1,
		links: 10,
		partners: 10,
		claims: 1_000,
		payoutCreditsCents: 50_000, // $500
		minPayoutThresholdCents: 2_500,
		maxPayoutThresholdCents: 100_000, // $1,000
	},
	standard: {
		apps: 3,
		links: 50,
		partners: 50,
		claims: 10_000,
		payoutCreditsCents: 100_000, // $1,000
		minPayoutThresholdCents: 2_500,
		maxPayoutThresholdCents: 250_000, // $2,500
	},
	scale: {
		apps: 10,
		links: Infinity,
		partners: Infinity,
		claims: 50_000,
		payoutCreditsCents: 300_000, // $3,000
		minPayoutThresholdCents: 2_500,
		maxPayoutThresholdCents: 500_000, // $5,000
	},
} as const;

export const PLAN_PRICING = {
	bootstrap: {
		monthly: 29.99,
		yearly: 269.99,
	},
	standard: {
		monthly: 49.99,
		yearly: 449.99,
	},
	scale: {
		monthly: 199.99,
		yearly: 1679.92,
	},
} as const;

/**
 * Enterprise plan uses Scale as base with custom overrides.
 * Limits are resolved dynamically via customLimits in user_subscriptions.
 */
export type PlanId = keyof typeof PLAN_LIMITS | "enterprise";
export type PlanLimits = (typeof PLAN_LIMITS)[keyof typeof PLAN_LIMITS];

/**
 * Mutable version of PlanLimits for computed limits (e.g., enterprise overrides)
 */
export interface MutablePlanLimits {
	apps: number;
	links: number;
	partners: number;
	claims: number;
	/**
	 * UI-only: Included payout volume in cents.
	 * Actual enforcement is via Stripe tiered metered pricing (SUM aggregation).
	 */
	payoutCreditsCents: number;
	/** Minimum payout threshold in cents (floor for all tiers: $25) */
	minPayoutThresholdCents: number;
	/** Maximum payout threshold in cents (varies by tier) */
	maxPayoutThresholdCents: number;
}

export const PLAN_IDS: PlanId[] = [
	...Object.keys(PLAN_LIMITS),
	"enterprise",
] as PlanId[];

/**
 * Standard plan ID (excludes enterprise which uses dynamic limits)
 */
export type StandardPlanId = keyof typeof PLAN_LIMITS;

/**
 * Get limits for a specific plan.
 * Note: For enterprise plans, use getEffectiveLimits() from subscription service.
 */
export function getPlanLimits(plan: StandardPlanId): PlanLimits {
	return PLAN_LIMITS[plan];
}

/**
 * Stripe price IDs for paid plans (set via environment variables)
 */
export type StripePriceIds = Record<Exclude<PlanId, "enterprise">, string>;

export const BILLING_PERIODS = ["monthly", "yearly"] as const;

export type BillingPeriod = (typeof BILLING_PERIODS)[number];
